import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  COLLECTIONS,
  LOCATION_GROUP_SCHEDULE_VIEW_HEADER_TAB_HEIGHT,
  LOCATION_GROUP_SCHEDULE_VIEW_TABLE_HEADER_HEIGHT,
  LOCATION_HEADER_LOCATION_GROUP_TAB_HEIGHT,
  LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT,
  USER_ACTIONS,
} from 'src/constants';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import {
  ILocation,
  ILocationGroup,
  IMappedLocationGroupDaypart,
} from 'src/interfaces';
import { IScheduleItem } from 'src/interfaces/Schedule';

import { SelectedRow } from './LocationSchedulePageTable.interface';

export const generateDeleteScheduleOrItemPayload = (
  selectedLocationGroup?: ILocationGroup,
  selectedLocation?: ILocation,
  selectedRow?: SelectedRow,
) => {
  const narrative = selectedRow?.scheduleItemId
    ? narrativeGenerators[COLLECTIONS.SCHEDULE_ITEMS][USER_ACTIONS.DELETE](
        selectedLocationGroup?.name,
        selectedRow?.componentName,
        selectedRow?.scheduleName,
      )
    : narrativeGenerators[COLLECTIONS.SCHEDULE_COMPONENTS][USER_ACTIONS.DELETE](
        selectedLocationGroup?.name,
        selectedRow?.scheduleName,
      );

  const params = selectedLocation
    ? {
        scheduleId: Number(selectedRow?.scheduleId),
        locationCode: selectedLocation?.location_code,
        narrative,
      }
    : {
        scheduleId: Number(selectedRow?.scheduleId),
        locationGroupId: selectedLocationGroup?.id,
        ...(selectedRow?.scheduleItemId && {
          scheduleItemId: selectedRow.scheduleItemId,
        }),
        narrative,
      };

  return params;
};

export const getTableContainerHeight = (isLocationGroupView: boolean) => {
  if (isLocationGroupView) {
    return `calc(100% - ${LOCATION_GROUP_SCHEDULE_VIEW_HEADER_TAB_HEIGHT}px - ${LOCATION_GROUP_SCHEDULE_VIEW_TABLE_HEADER_HEIGHT}px)`;
  }

  return `calc(100% - ${LOCATION_HEADER_LOCATION_GROUP_TAB_HEIGHT}px - ${LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT}px - ${LOCATION_GROUP_SCHEDULE_VIEW_HEADER_TAB_HEIGHT}px - ${LOCATION_GROUP_SCHEDULE_VIEW_TABLE_HEADER_HEIGHT}px)`;
};

/**
 * Groups items by their end date.
 * @param items - Array of location group daypart items
 * @returns Map with end date strings as keys and arrays of items with that end date as values
 */
export const groupByEndDate = (
  items: IMappedLocationGroupDaypart[],
): Map<string, IMappedLocationGroupDaypart[]> => {
  return items.reduce((map, item) => {
    const key = item.endDate;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(item);

    return map;
  }, new Map<string, IMappedLocationGroupDaypart[]>());
};

/**
 * Finds the item with the latest assembly schedule date (most recently modified).
 * @param items - Array of location group daypart items
 * @returns The item with the most recent assemblyScheduleDate
 */
export const getLatestModified = (
  items: IMappedLocationGroupDaypart[],
): IMappedLocationGroupDaypart => {
  return items.reduce((latest, item) =>
    dayjs(item.assemblyScheduleDate).isAfter(dayjs(latest.assemblyScheduleDate))
      ? item
      : latest,
  );
};

dayjs.extend(isSameOrAfter);

export const isPreviewEnabled = (schedule: IScheduleItem[]): boolean => {
  if (!schedule || schedule.length === 0) return false;

  const today = dayjs();

  // Check start dates: find the earliest start date
  const validStartDates = schedule
    .map((item) => item.start_date)
    .filter((d) => d && d.trim() !== '')
    .map((d) => dayjs(d));

  if (validStartDates.length > 0) {
    const earliestStartDate = validStartDates.reduce((a, b) =>
      a.isBefore(b) ? a : b,
    );

    const threeYearsLater = today.add(3, 'year');
    if (earliestStartDate.isAfter(threeYearsLater, 'day')) {
      return false; // earliest start date too far → disable
    }
  }

  //  If any schedule item has a null/empty end date, enable
  const hasNullEndDate = schedule.some(
    (item) => !item.end_date || item.end_date.trim() === '',
  );
  if (hasNullEndDate) return true;

  //  Validate end dates: find latest end date
  const validEndDates = schedule
    .map((item) => item.end_date)
    .filter((d) => d && d.trim() !== '')
    .map((d) => dayjs(d, 'MM-DD-YYYY'));

  if (validEndDates.length === 0) return true;

  const latestEndDate = validEndDates.reduce((a, b) => (a.isAfter(b) ? a : b));

  const thirtyDaysAgo = today.subtract(30, 'day');

  //  Check if latest end date is within past 30 days (inclusive)
  return latestEndDate.isSameOrAfter(thirtyDaysAgo, 'day');
};

export const getEarliestStartDate = (
  scheduleItems: IScheduleItem[],
): string | null => {
  if (!scheduleItems || scheduleItems.length === 0) return null;

  let earliest: dayjs.Dayjs | null = null;

  for (const item of scheduleItems) {
    if (!item.start_date) continue;

    const date = dayjs(item.start_date, 'MM-DD-YYYY', true);
    if (!date.isValid()) continue;

    if (!earliest || date.isBefore(earliest)) {
      earliest = date;
    }
  }

  return earliest ? earliest.format('MM-DD-YYYY') : null;
};
